// CONF-210. Which flash (F-node) hardware the AFA set uses. Absent means
// 'standard' (VCH-5000-F1N); 'dualCpu' is the gated VCH-5500-F1N. A string
// enum rather than a boolean so a third node generation is a table row in
// FLASH_NODE_MODELS (src/utils/partNumbers.jsx), not a second flag.
export type AfaNodeModel = 'standard' | 'dualCpu';

// Typed shape of today's getInitialConfig() output (useConfigurationLogic.jsx).
// This is the boundary type the translator reads/writes. Keep field names verbatim.
export interface FlatConfig {
  // system
  systemType: string; interfaceOption: string; networkProtocol: string; rackSize: number;
  sizeByNodeCount: boolean; hardwareVendor: string;
  compressionRatio: number; endToEndEncryption: boolean;
  // directors
  veloCount: number; veloSsdCapacity: number;
  // hybrid storage
  hybridEnabled: boolean; hybridMinRawCapacity: number; hybridNodeCount: number;
  jbodSize: number; hybridHddCapacity: number; hybridVpodSsdCapacity: number;
  hybridEncodingScheme: string; hybridSpares: number; hybridMLEC: boolean;
  hybridCompressionRatio: number; hybridEndToEndEncryption: boolean;
  // Optional: stamped by useConfigurationLogic on capacity/vendor changes, not
  // present in getInitialConfig(). Read by partNumberUtils and the config cards.
  hybridHddPartNumber?: string; hybridSsdPartNumber?: string;
  // afa storage
  afaEnabled: boolean; afaMinRawCapacity: number; afaNodeCount: number;
  afaVpodSsdCapacity: number; afaEncodingScheme: string; afaSpares: number;
  afaCompressionRatio: number; afaEndToEndEncryption: boolean;
  afaSsdPartNumber?: string; // optional, stamped dynamically (see hybrid note)
  // Optional on purpose and never defaulted: getInitialConfig() and
  // migrateQuoteConfig() leave it absent so the frozen initial-config and
  // metrics-parity snapshots stay byte-identical. Set only by the exec toggle.
  afaNodeModel?: AfaNodeModel;
  // pricing
  subscriptionMonths: number; serviceOption: string; quarter: string;
  discountMonths: number; ssdSoftware: number; hddSoftware: number;
  baseSsdSoftware: number; baseHddSoftware: number; ssdLicenseUnitPrice: number;
  ssdSupportUnitPrice: number; hddLicenseUnitPrice: number; hddSupportUnitPrice: number;
  // metadata
  preparedFor: string; partnerName: string; quoteDescription: string; notes: string;
  opportunityID: string; customerID: string; customerNumber: string; offerNumber: string;
  configuratorQuoteSerial: string; salesforceQuoteNumber: string;
  accountExecutive: string; accountExecutiveEmail: string;
  warrantyStartDate: string; quoteExpirationDate: string;
  // view state
  isNoReg: boolean; consultationMode: boolean; isPBUnit: boolean; cciEnabled: boolean;
  hybridEncodingSchemeUserSelected: boolean; afaEncodingSchemeUserSelected: boolean;
  // constants
  availableSpares: number[];
}

// Runtime mirror of the FlatConfig keys, used by ConfigurationMapper to detect
// fields the domain model doesn't know about (added on main after this file).
// The type assertions below force this list to stay in lockstep with the
// interface: an entry that isn't a FlatConfig key, or a FlatConfig key missing
// from the list, stops compilation.
export const FLAT_CONFIG_KEYS = [
  'systemType', 'interfaceOption', 'networkProtocol', 'rackSize', 'sizeByNodeCount', 'hardwareVendor',
  'compressionRatio', 'endToEndEncryption',
  'veloCount', 'veloSsdCapacity',
  'hybridEnabled', 'hybridMinRawCapacity', 'hybridNodeCount', 'jbodSize',
  'hybridHddCapacity', 'hybridVpodSsdCapacity', 'hybridEncodingScheme',
  'hybridSpares', 'hybridMLEC', 'hybridCompressionRatio', 'hybridEndToEndEncryption',
  'hybridHddPartNumber', 'hybridSsdPartNumber',
  'afaEnabled', 'afaMinRawCapacity', 'afaNodeCount', 'afaVpodSsdCapacity',
  'afaEncodingScheme', 'afaSpares', 'afaCompressionRatio', 'afaEndToEndEncryption',
  'afaSsdPartNumber', 'afaNodeModel',
  'subscriptionMonths', 'serviceOption', 'quarter', 'discountMonths',
  'ssdSoftware', 'hddSoftware', 'baseSsdSoftware', 'baseHddSoftware',
  'ssdLicenseUnitPrice', 'ssdSupportUnitPrice', 'hddLicenseUnitPrice', 'hddSupportUnitPrice',
  'preparedFor', 'partnerName', 'quoteDescription', 'notes',
  'opportunityID', 'customerID', 'customerNumber', 'offerNumber',
  'configuratorQuoteSerial', 'salesforceQuoteNumber',
  'accountExecutive', 'accountExecutiveEmail',
  'warrantyStartDate', 'quoteExpirationDate',
  'isNoReg', 'consultationMode', 'isPBUnit', 'cciEnabled',
  'hybridEncodingSchemeUserSelected', 'afaEncodingSchemeUserSelected',
  'availableSpares',
] as const;

type AssertTrue<T extends true> = T;
// Every listed key exists on FlatConfig…
export type _KeysAreValid = AssertTrue<
  typeof FLAT_CONFIG_KEYS[number] extends keyof FlatConfig ? true : false
>;
// …and every FlatConfig key is listed.
export type _KeysAreComplete = AssertTrue<
  Exclude<keyof FlatConfig, typeof FLAT_CONFIG_KEYS[number]> extends never ? true : false
>;
